import * as React from "react";
import {Box, Stack, Typography} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useTranslation} from "react-i18next";

interface PayrollRunStatusRowProps {
    isPeriodOpen: boolean;
    isPeriodLocked: boolean;
    hasResults: boolean;
}

/**
 * Small status indicator row shown under the tabs on the Payroll Run screen (desktop and
 * mobile) — open/locked period + "preliminary results" notice. Deliberately not a period/branch
 * selector: the top toolbar (desktop) or the filter bar under the tabs (mobile) is the single
 * source of truth for that selection (see docs/PAYROLL_RUN_LAYOUT.md).
 */
export default function PayrollRunStatusRow({
    isPeriodOpen,
    isPeriodLocked,
    hasResults,
}: Readonly<PayrollRunStatusRowProps>): React.ReactElement | null {
    const {t} = useTranslation();

    if (!isPeriodOpen && !isPeriodLocked) return null;

    return (
        <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={1.5} sx={{mb: 2}}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: isPeriodOpen ? "success.main" : "text.disabled",
                    }}
                />
                <Typography variant="body2" fontWeight={600}>
                    {isPeriodOpen ? t('payroll.run.statusBar.openPeriod') : t('payroll.run.statusBar.lockedPeriod')}
                </Typography>
            </Stack>
            {hasResults && (
                <>
                    <Typography variant="body2" color="text.disabled">|</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <InfoOutlinedIcon fontSize="small" color="warning"/>
                        <Typography variant="body2" color="warning.dark" fontWeight={600}>
                            {t('payroll.run.statusBar.preliminaryResults')}
                        </Typography>
                    </Stack>
                </>
            )}
        </Stack>
    );
}
